import React, { useState, useEffect } from 'react';
import { Save, X, AlertTriangle, Loader2 } from 'lucide-react';
import { Button } from '../ui/Button'; // Path relative to src/components/admin
import { Input } from '../ui/input';   // Path relative to src/components/admin
import { Textarea } from '../ui/textarea'; // Path relative to src/components/admin
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card'; // Path relative to src/components/admin
import { Product } from '../../types/Product'; // Import Product from new location

interface ProductFormProps {
  product: Product;
  onSave: (product: Product) => void;
  onCancel: () => void;
  isSaving: boolean;
  error: string | null;
  existingCategories: string[]; // Made non-optional, will default in destructuring
}

const ProductForm: React.FC<ProductFormProps> = ({
  product,
  onSave,
  onCancel,
  isSaving,
  error,
  existingCategories = [], // Default to empty array
}) => {
  // console.log('Existing categories in form:', existingCategories);
  const [name, setName] = useState(product.name);
  const [description, setDescription] = useState(product.description);
  const [price, setPrice] = useState(product.price.toString());
  const [imageUrl, setImageUrl] = useState(product.imageUrl);

  // Category related states
  const [category, setCategory] = useState(''); // Holds the selected value from dropdown, or special value
  const [showNewCategoryInput, setShowNewCategoryInput] = useState(false);
  const [newCategoryValue, setNewCategoryValue] = useState('');

  const [specifications, setSpecifications] = useState<Record<string, string>>(product.specifications || {});
  const [specField, setSpecField] = useState('');
  const [specValue, setSpecValue] = useState('');

  // Effect to update form fields when the product prop changes
  useEffect(() => {
    setName(product.name);
    setDescription(product.description);
    setPrice(product.price.toString());
    setImageUrl(product.imageUrl);
    setSpecifications(product.specifications || {});

    const currentCategory = product.category || '';
    if (existingCategories.includes(currentCategory)) {
      setCategory(currentCategory);
      setShowNewCategoryInput(false);
      setNewCategoryValue('');
    } else if (currentCategory) { // It's a custom category not in the list (for existing product)
      setCategory('_addNewCategory_');
      setShowNewCategoryInput(true);
      setNewCategoryValue(currentCategory);
    } else { // No category (new product or product without category)
      setCategory(''); // Default to '-- Select Existing Category --'
      setShowNewCategoryInput(false);
      setNewCategoryValue('');
    }
  }, [product, existingCategories]);

  const handleSave = () => {
    let categoryToSave = '';
    if (showNewCategoryInput && newCategoryValue.trim()) {
      categoryToSave = newCategoryValue.trim();
    } else if (category && category !== '_addNewCategory_') {
      categoryToSave = category;
    } else if (category === '_addNewCategory_' && !newCategoryValue.trim()) {
      // User selected "add new" but didn't type a new category name
      // Optionally, set an error or alert the user. For now, we'll save it as empty or the current newCategoryValue (which would be empty).
      // To prevent saving with an "empty" new category, you might add an error state.
      // For this iteration, if newCategoryValue is empty, categoryToSave will be empty.
      // This could be handled with form validation before calling onSave.
      console.warn("Attempting to save with 'Add New Category' selected but no new category name entered.");
    }


    const updatedProduct: Product = {
      ...product,
      name,
      description,
      price: parseFloat(price) || 0,
      imageUrl,
      category: categoryToSave,
      specifications,
    };
    onSave(updatedProduct);
  };

  const addSpecification = () => {
    if (specField.trim() && specValue.trim()) {
      setSpecifications({
        ...specifications,
        [specField.trim()]: specValue.trim(),
      });
      setSpecField('');
      setSpecValue('');
    }
  };

  const deleteSpecification = (field: string) => {
    const { [field]: _, ...rest } = specifications;
    setSpecifications(rest);
  };

  return (
    <Card className="product-form-card">
      <CardHeader>
        <CardTitle>{product.id === 'new' ? 'Add New Product' : `Edit Product - ${product.name}`}</CardTitle>
        <CardDescription>
          {product.id === 'new' ? 'Fill in the details to add a new product.' : 'Modify the product details below.'}
        </CardDescription>
      </CardHeader>
      <CardContent className="product-form-content">
        {error && (
          <div className="error-message" role="alert" style={{ color: 'red', marginBottom: '1rem' }}>
            <AlertTriangle className="icon" style={{ marginRight: '0.5rem' }}/>
            {error}
          </div>
        )}
        <div>
          <label htmlFor="product-name" className="form-label">Name</label>
          <Input
            id="product-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="form-input"
            placeholder="Product Name"
            required
          />
        </div>
        <div>
          <label htmlFor="product-description" className="form-label">Description</label>
          <Textarea
            id="product-description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="form-textarea"
            placeholder="Product Description"
            required
          />
        </div>
        <div>
          <label htmlFor="product-price" className="form-label">Price</label>
          <Input
            id="product-price"
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="form-input"
            placeholder="Product Price"
            required
            min="0"
            step="0.01"
          />
        </div>
        <div>
          <label htmlFor="product-imageUrl" className="form-label">Image URL</label>
          <Input
            id="product-imageUrl"
            value={imageUrl}
            onChange={(e) => setImageUrl(e.target.value)}
            className="form-input"
            placeholder="Product Image URL"
          />
        </div>
        <div>
          <label htmlFor="product-category-select" className="form-label">Category</label>
          <select
            id="product-category-select"
            value={category} // This state now holds the selected value from dropdown, or '_addNewCategory_'
            onChange={(e) => {
              const selectedVal = e.target.value;
              setCategory(selectedVal);
              if (selectedVal === '_addNewCategory_') {
                setShowNewCategoryInput(true);
                setNewCategoryValue(''); // Clear previous new category value on selecting "add new"
              } else {
                setShowNewCategoryInput(false);
                setNewCategoryValue(''); // Clear new category value if an existing one is selected
              }
            }}
            className="form-input" // You might need specific styling for select
            required={!showNewCategoryInput && !category} // Required if not adding new and no category selected
          >
            <option value="">-- Select Existing Category --</option>
            {existingCategories.map(cat => <option key={cat} value={cat}>{cat}</option>)}
            <option value="_addNewCategory_">-- Add New Category --</option>
          </select>
        </div>

        {showNewCategoryInput && (
          <div style={{ marginTop: '0.5rem' }}>
            <label htmlFor="product-new-category" className="form-label">New Category Name</label>
            <Input
              id="product-new-category"
              value={newCategoryValue}
              onChange={(e) => setNewCategoryValue(e.target.value)}
              className="form-input"
              placeholder="Enter New Category Name"
              required={showNewCategoryInput && !newCategoryValue.trim()} // Required if shown and empty
            />
          </div>
        )}
        <div>
          <label className="form-label">Specifications</label>
          {Object.entries(specifications).map(([key, value]) => (
            <div key={key} className="spec-item">
              <Input value={key} readOnly className="spec-input-readonly"/>
              <Input value={value} readOnly className="spec-input-readonly"/>
              <Button variant="outline" size="icon" onClick={() => deleteSpecification(key)} className="spec-delete-button"><X className="icon" /></Button>
            </div>
          ))}
          <div className="spec-add-row">
            <Input value={specField} onChange={(e) => setSpecField(e.target.value)} placeholder="Field" className="spec-input"/>
            <Input value={specValue} onChange={(e) => setSpecValue(e.target.value)} placeholder="Value" className="spec-input"/>
            <Button variant="outline" onClick={addSpecification}>Add Specification</Button>
          </div>
        </div>
      </CardContent>
      <CardFooter className="product-form-footer">
        <Button variant="outline" onClick={onCancel} disabled={isSaving}>
          Cancel
        </Button>
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (<><Loader2 className="icon-spin" style={{ marginRight: '0.5rem' }} />Saving...</>) : (<><Save className="icon" style={{ marginRight: '0.5rem' }} />Save Product</>)}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default ProductForm;
